import { PlusInSquareIcon, ReplaceIcon } from "assets/icons";
import { toastServerError } from "helpers";
import { useBoolean } from "hooks";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "ui";

import {
  useDeleteMarketingTemplate,
  useMarketingTemplates,
  useSyncMarketingTemplates
} from "../../apis";
import { ChipGroup, TemplateCards, TemplateModal } from "../../components";
import { TEMPLATE_NAME_LOCK_WEEKS } from "../../constants";
import {
  EMarketingTemplateStatus,
  MarketingTemplate
} from "../../marketing.types";

import styles from "./MarketingTemplates.module.scss";

const STATUS_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Approved", value: EMarketingTemplateStatus.Approved },
  { label: "Pending", value: EMarketingTemplateStatus.Pending },
  { label: "Draft", value: EMarketingTemplateStatus.Draft },
  { label: "Rejected", value: EMarketingTemplateStatus.Rejected }
];

const MarketingTemplates = () => {
  const { t } = useTranslation();

  const [statusFilter, setStatusFilter] = useState("all");
  const { templates, isLoading } = useMarketingTemplates();

  const filteredTemplates =
    statusFilter === "all"
      ? templates
      : templates.filter((template) => template.status === statusFilter);

  const {
    value: isTemplateModalOpen,
    setTrue: openTemplateModal,
    setFalse: closeTemplateModal
  } = useBoolean(false);

  const [editableTemplate, setEditableTemplate] =
    useState<MarketingTemplate | null>(null);

  const [templateToDelete, setTemplateToDelete] =
    useState<MarketingTemplate | null>(null);

  const { mutate: syncTemplates, isLoading: isSyncing } =
    useSyncMarketingTemplates({
      onSuccess: (data) =>
        toast.success(`${t("Synced from Meta")}: ${data.synced}`),
      onError: toastServerError
    });

  const { mutate: deleteTemplate, isLoading: isDeleting } =
    useDeleteMarketingTemplate({
      onSuccess: () => {
        toast.success(t("Template deleted"));
        setTemplateToDelete(null);
      },
      onError: toastServerError
    });

  const handleEdit = (template: MarketingTemplate) => {
    setEditableTemplate(template);
    openTemplateModal();
  };

  const handleCloseTemplateModal = () => {
    setEditableTemplate(null);
    closeTemplateModal();
  };

  return (
    <div className={styles.templates}>
      <div className={styles.templates__controls}>
        <p className={styles.templates__hint}>
          {t(
            "The reusable content library for flows and campaigns. A template must be approved by Meta (24–48h) before it can be sent outside a customer's 24h window."
          )}
        </p>

        <div className={styles.templates__actions}>
          <Button
            variant="default"
            icon={<ReplaceIcon width={16} height={16} />}
            loading={isSyncing}
            onClick={() => syncTemplates()}
          >
            {t("Sync from Meta")}
          </Button>

          <Button icon={<PlusInSquareIcon />} onClick={openTemplateModal}>
            {t("Register template")}
          </Button>
        </div>
      </div>

      <ChipGroup
        label={t("Status")}
        options={STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
      />

      <TemplateCards
        templates={filteredTemplates}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={setTemplateToDelete}
      />

      {isTemplateModalOpen && (
        <TemplateModal
          isOpen={isTemplateModalOpen}
          editableTemplate={editableTemplate}
          onClose={handleCloseTemplateModal}
        />
      )}

      <Modal
        type="delete"
        isOpen={Boolean(templateToDelete)}
        isLoading={isDeleting}
        title={t("Delete template")}
        okText={t("Delete")}
        onClose={() => setTemplateToDelete(null)}
        onOk={() => templateToDelete && deleteTemplate(templateToDelete.name)}
      >
        <p className={styles.confirmText}>
          {templateToDelete
            ? `${t("Delete template")} "${templateToDelete.name}"? ${t(
                "Meta keeps the template and locks its name for about"
              )} ${TEMPLATE_NAME_LOCK_WEEKS} ${t(
                "weeks — you will not be able to re-register it under the same name."
              )}`
            : ""}
        </p>
      </Modal>
    </div>
  );
};

export default MarketingTemplates;
